import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../../../utils/axios';
import { errorToast } from '../../../../utils/toasts/toasts';

const initialState = {
    events: [],
    userEvents: [],
    loading: false,
    error: null,
    eventData: null,
    status: null,
    activeTab: 1,
    maxTabLimit: 1,
    eventSchedules: [],
    userInterestedEvents: [],
    eventCreationTabs: [
        { name: 'Basic Details', status: 'active' },
        { name: 'Schedule', status: 'inactive' },
        { name: 'Description', status: 'inactive' },
        { name: 'Media', status: 'inactive' },
        { name: 'Location', status: 'inactive' }
    ]
};
// eventData => will be use as create event data or update event data or get event data
// events => will be use as events list data
// status => will be use as api response status
// schedules => will be use as event schedules data

export const steps = [
    { step: 'detail', label: 'Basic Details' },
    { step: 'timing', label: 'Schedule' },
    { step: 'description', label: 'Description' },
    { step: 'media', label: 'Media' },
    { step: 'location', label: 'Location' }
];

// Add helper functions before the eventSlice
const calculateTabState = (isPublishable) => {
    if (!isPublishable) return { activeTab: 1, maxTabLimit: 1 };

    const steps = ['details', 'schedule', 'description', 'media', 'location'];
    let maxTabLimit = 1;
    let activeTab = 1;

    // Find the maximum completed step
    for (let i = 0; i < steps.length; i++) {
        if (isPublishable[steps[i]] === 1) {
            maxTabLimit = i + 1;
        }
    }

    // Find the first incomplete step as active tab
    for (let i = 0; i < steps.length; i++) {
        if (isPublishable[steps[i]] === 0) {
            activeTab = i + 1;
            break;
        }
    }

    // If all steps are complete, set active tab to the last completed step
    if (activeTab === 1 && maxTabLimit > 1) {
        activeTab = maxTabLimit;
    }

    // If all values are 0, ensure activeTab and maxTabLimit are both 1
    if (maxTabLimit === 1 && activeTab === 1) {
        return { activeTab: 1, maxTabLimit: 1 };
    }

    return { activeTab, maxTabLimit };
};

const updateTabStatus = (tabs, activeTab, maxTabLimit) => {
    return tabs.map((tab, index) => {
        if (index === activeTab - 1) {
            return { ...tab, status: 'active' };
        } else if (index < maxTabLimit) {
            return { ...tab, status: 'completed' };
        } else {
            return { ...tab, status: 'inactive' };
        }
    });
};

const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        setEventData: (state, action) => {
            state.eventData = action.payload;
        },
        clearEventData: (state) => {
            state.eventData = null;
        },
        nextTab: (state) => {
            const newTab = state.activeTab + 1;

            if (newTab > state.maxTabLimit) {
                state.maxTabLimit = newTab;
            }

            state.eventCreationTabs = state.eventCreationTabs.map((tab, index) => {
                if (index === newTab - 1) {
                    return { ...tab, status: 'active' };
                } else if (index < newTab - 1) {
                    return { ...tab, status: 'completed' };
                }
                return tab;
            });

            state.activeTab = newTab;
        },
        previousTab: (state) => {
            if (state.activeTab > 1) {
                const newTab = state.activeTab - 1;

                state.eventCreationTabs = state.eventCreationTabs.map((tab, index) => {
                    if (index === newTab - 1) {
                        return { ...tab, status: 'active' };
                    } else if (index === state.activeTab - 1) {
                        return { ...tab, status: 'completed' };
                    }
                    return tab;
                });

                state.activeTab = newTab;
            }
        },
        onTabClick: (state, action) => {
            const clickedTab = action.payload + 1;
            state.activeTab = clickedTab;

            state.eventCreationTabs = state.eventCreationTabs.map((tab, index) => {
                if (index === action.payload) {
                    return { ...tab, status: 'active' };
                }
                if (index < state.maxTabLimit) {
                    return { ...tab, status: 'completed' };
                }
                return tab;
            });
        },
        setActiveTabAndMaxTabLimit: (state, action) => {
            // If payload provided, set active/max based on payload,
            // otherwise keep legacy behavior (reset to 1).
            if (action && action.payload) {
                const { activeTab = 1, maxTabLimit = 1 } = action.payload;
                state.activeTab = activeTab;
                state.maxTabLimit = maxTabLimit;
                state.eventCreationTabs = updateTabStatus(
                    state.eventCreationTabs,
                    activeTab,
                    maxTabLimit
                );
            } else {
                state.activeTab = 1;
                state.maxTabLimit = 1;
                state.eventCreationTabs = updateTabStatus(state.eventCreationTabs, 1, 1);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEventsList.pending, (state) => {
                state.loading = true;
            })
            .addCase(getEventsList.fulfilled, (state, action) => {
                state.loading = false;
                state.userEvents = action.payload;
                // console.log('Events fetched successfully:', action.payload);
            })
            .addCase(getEventsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        builder
            .addCase(createEvent.pending, (state) => {
                state.loading = true;
            })
            .addCase(createEvent.fulfilled, (state, action) => {
                state.loading = false;
                state.eventData = action.payload.data;
                state.status = action.payload.success;
                console.log('Event created successfully:', action.payload);
            })
            .addCase(createEvent.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                // state.status = action.payload.success || action.payload.status;
                console.error('Error creating event:', action.payload);
            });

        builder
            .addCase(updateEventById.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateEventById.fulfilled, (state, action) => {
                state.loading = false;
                state.eventData = { ...state.eventData, ...action.payload.data };
                state.status = action.payload.success;
                console.log('Event updated successfully:', action.payload.data);
            })
            .addCase(updateEventById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                console.error('Error updating event:', action.payload);
            });

        builder
            .addCase(getEventBySlug.pending, (state) => {
                state.loading = true;
            })
            .addCase(getEventBySlug.fulfilled, (state, action) => {
                state.loading = false;
                state.eventData = action.payload.data;
                // Calculate and set tab state based on is_publishable
                if (action.payload.data?.is_publishable) {
                    const {
                        // activeTab, 
                        maxTabLimit
                    } = calculateTabState(
                        action.payload.data.is_publishable
                    );
                    // state.activeTab = activeTab;
                    state.maxTabLimit = maxTabLimit;
                    // state.eventCreationTabs = updateTabStatus(
                    //     state.eventCreationTabs,
                    //     activeTab,
                    //     maxTabLimit
                    // );
                    state.eventCreationTabs = updateTabStatus(
                        state.eventCreationTabs,
                        1,
                        maxTabLimit
                    );
                }
                // console.log('Event fetched successfully:', action.payload);
            })
            .addCase(getEventBySlug.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        builder
            .addCase(getEventScheduleById.pending, (state) => {
                state.loading = true;
            })
            .addCase(getEventScheduleById.fulfilled, (state, action) => {
                state.loading = false;
                state.eventSchedules = action.payload.data;
                // console.log("Event schedules fetched successfully:", action.payload);
            })
            .addCase(getEventScheduleById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                console.error('Error fetching event schedules:', action.payload);
            });

        builder
            .addCase(updateEventDetailsById.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateEventDetailsById.fulfilled, (state, action) => {
                state.loading = false;
                // state.eventData = action.payload.data;
                // state.status = action.payload.success;
                state.eventData = { ...state.eventData, ...action.payload.data };
                console.log('Event details updated successfully:', action.payload);
            })
            .addCase(updateEventDetailsById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                console.error('Error updating event details:', action.payload);
            });

        builder
            .addCase(deleteEventById.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteEventById.fulfilled, (state, action) => {
                const { eventId } = action.payload;
                state.loading = false;
                state.eventData = null; // Clear event data after deletion
                // state.status = action.payload.success;
                state.events = state.events.filter((event) => event.eventId !== eventId);
                console.log('Event deleted successfully:', action.payload);
            });

        builder
            .addCase(getUserInterestedEventsByUserId.fulfilled, (state, action) => {
                state.userInterestedEvents = action.payload.data;
            });

        builder
            .addCase(markEventAsInterested.fulfilled, (state, action) => {
                state.userInterestedEvents.push(action.payload.data);
            });
    }
});

export const {
    setEventData,
    clearEventData,
    nextTab,
    previousTab,
    onTabClick,
    setActiveTabAndMaxTabLimit
} = eventSlice.actions;

export default eventSlice.reducer;

export const getUserInterestedEventsByUserId = createAsyncThunk(
    'event/getUserInterestedEventsByUserId',
    async (userId, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.get(`/api/v2/interests/${userId}`);

            const formatResponse = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return formatResponse;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)


/**
 * {
    "user_id": 1,
    "entity": "event",
    "entity_id": 12,
    "interested": true
}
 */
/**
 * @async
 * @function to mark an event as interested by sending a POST request to the backend.
 * @param {object} payload - The payload containing user_id, entity, entity_id, and interested status.
 * @returns {object} - The formatted response from the backend.
 * @throws {object} - An error object if the request fails.
 * Steps:
 * 1. Make a POST request api call
 * 2. Format the response
 * 3. Return the response
 */
export const markEventAsInterested = createAsyncThunk(
    'event/markEventAsInterested',
    async (payload, { rejectWithValue }) => {
        try {

            const backendResponse = await axios.post(`/api/v2/interests`, payload);

            const formatResponse = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return formatResponse;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)

export const rsvpToEvent = createAsyncThunk(
    'event/rsvpToEvent',
    async ({ response }, { rejectWithValue }) => {
        try {
            const sessionId = localStorage.getItem("sessionId");

            const backendResponse = await axios.post(`/api/v2/invitations/respond`,
                { response },  // Pass the response (yes/no) to the backend
                {
                    headers: {
                        "X-Session-ID": sessionId
                    }
                }
            );

            const formatResponse = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return formatResponse;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);


/**
 * @async function: to fetch the RSVP response of the user for the event using the session ID.
 * @param {string} sessionId - The session ID stored in localStorage after verifying the invite code.
 * @returns {object} - The RSVP response data from the backend.
 * @throws {object} - An error object if the request fails.
 * 
 * Steps
 * 1. Make a POST request api call
 * 2. Format the response
 * 3. Return the response
 */
export const getEventRsvpUserResponse = createAsyncThunk(
    'event/getEventRsvpUserResponse',
    async (sessionId, { rejectWithValue }) => {
        try {

            /**
             * Step 1
             */
            const backendResponse = await axios.post(`/api/v2/invitation/respond-with-user`, {}, {
                headers: {
                    "X-Session-ID": sessionId
                }
            });


            /**
             * Step 2
             */
            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status,
                statusText: backendResponse?.statusText
            }

            // console.log("Fetched RSVP response:", response);

            /**
             * Step 3
             */
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status,
                statusText: error?.statusText
            });
        }
    }
)

export const getEventsList = createAsyncThunk(
    'event/getEventsList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/v2/events`);
            return response.data;
        } catch (error) {
            errorToast(error?.data?.message || 'Failed to fetch events');
            return rejectWithValue(error.response?.data || { message: 'Failed to fetch events' });
        }
    }
);

export const getAllPublicEvents = createAsyncThunk(
    'event/getAllPublicEvents',
    async ({ method = 'get', payload = null } = {}, { rejectWithValue }) => {
        try {
            const methodLower = method.toLowerCase();
            let backendResponse;

            if (methodLower === 'post' && payload) {
                backendResponse = await axios.post(`/api/v2/events/public`, payload);
            } else {
                backendResponse = await axios[methodLower](`/api/v2/events/public`);
            }

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status,
                statusText: backendResponse?.statusText
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status,
                statusText: error?.statusText
            });
        }
    }
);

export const createEvent = createAsyncThunk(
    'event/createEvent',
    async (eventData, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.post(`/api/v2/events`, eventData);
            dispatch(getEventsList()); // Refresh events list after creation
            return response.data;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);

export const updateEventById = createAsyncThunk(
    'event/updateEventById',
    async ({ eventId, eventData }, { rejectWithValue }) => {
        try {
            // Send FormData with multipart/form-data content type
            const response = await axios.post(`/api/v2/events/${eventId}`, eventData);
            return response.data;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);

export const updateEventDetailsById = createAsyncThunk(
    'event/updateEventDetailsById',
    async ({ eventId, eventData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `/api/v2/events/detail/${eventId}`,
                eventData
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error?.data || error);
        }
    }
);

export const getEventBySlug = createAsyncThunk(
    'event/getEventBySlug',
    async (slug, { rejectWithValue }) => {
        try {
            const backedResponse = await axios.get(`/api/v2/events/${slug}`);
            const response = {
                ...backedResponse?.data,
                code: backedResponse?.status
            }
            return response
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);

export const getEventScheduleById = createAsyncThunk(
    'event/getEventScheduleById',
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/events/schedules/${eventId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const deleteEventById = createAsyncThunk(
    'event/deleteEventById',
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`/api/v2/events/${eventId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// const createEvent = [
//     {
//         step: 'details',
//         requestBody: {
//             title: 'Event Title',
//             excerpt: 'Event Short Name',
//             category_id: 'Category ID',
//             subcategory_id: ["1", "2", "3"],
//             is_private: "true or false",
//             format_names: ["Format 1", "Format 2", "Format 3"],
//             create_event_as: "To be discussed",
//             online_event: "true or false"
//         }
//     },
//     {
//         step: "schedule",
//         requestBody: {
//             event_id: "Event ID",
//             user_id: "User ID",
//             from_date: "YYYY-MM-DD",
//             from_time: "HH:MM",
//             to_date: "YYYY-MM-DD",
//             to_time: "HH:MM",
//         }
//     },
//     {
//         step: "description",
//         requestBody: {
//             event_id: "Event ID",
//             user_id: "User ID",
//             description: "Event Description",
//             event_creation_tnc: "Terms and condition",
//             faq: "FAQs",
//             emergency_contact_number: '1111111111',
//             tag_ids: ["Tag ID 1", "Tag ID 2", "Tag ID 3"],
//         }
//     },
//     {
//         step: "media",
//         requestBody: {
//             event_id: "Event ID",
//             user_id: "User ID",
//             thumbnail: "Thumbnail Image Whole File",
//             posters: ["Poster Image Whole File Multiple"],
//             images: ["Image Whole File Multiple"],
//             event_layout: "Layout Image Whole File"
//         }
//     },
//     {
//         step: "location",
//         ifOnlineEvent: {
//             requestBody: {
//                 event_id: "Event ID",
//                 user_id: "User ID",
//                 online_event_link: "Online Event Link",
//                 platform_name: "Platform Name (e.g., Zoom, Google Meet)",
//                 event_credentials: "Event Joining Link Credentials",
//                 event_joining_instruction: "Joining Instruction",
//                 video_link: "Joining Link"
//             }
//         },
//         ifOfflineEvent: {
//             requestBody: {
//                 event_id: "Event ID",
//                 user_id: "User ID",
//                 venue_name: "",
//                 area: "",
//                 street: "",
//                 address: "",
//                 latitude: "",
//                 longitude: "",
//                 city_id: "",
//                 state_id: "",
//                 country_id: "",
//                 zipcode: ""
//             }
//         }
//     }
// ]
